/** ADM-29 Validations ◆ · ADM-38 Utilisateurs internes · ADM-39 Rôles · ADM-43 Clés de chiffrement (SIMULÉ). */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Button, Card, Choice, Modal, Pagination, SearchField, SelectField, Table, TextField, useToast } from '@myron/design-system';
import { KeyRound, RotateCcw, ShieldCheck, UserPlus } from 'lucide-react';
import { LIBELLE_ROLE, ROLES_INTERNES, type Role } from '@myron/domain';
import type { S } from '@myron/contracts';
import { ApiErreur, del, get, post, put } from '../../../shared/api/client';
import { useSession } from '../../../shared/auth/session';
import { Confirmation, EntetePage, ErreurFormulaire, EtatRequete, MentionSimule, StatutBadge } from '../../../shared/ui/composants';
import { dateHeure } from '../../../shared/ui/format';

const LIBELLE_TYPE_VALIDATION: Record<string, string> = {
  AJUSTEMENT_STOCK: 'Ajustement de stock', REMBOURSEMENT: 'Remboursement', IBAN_FOURNISSEUR: 'IBAN fournisseur',
  UTILISATEUR_INTERNE: 'Compte interne', REINITIALISATION_MFA: 'Réinitialisation MFA', ROTATION_CLE: 'Rotation de clé',
};

// ───────────────────────────────── Validations ◆ ─────────────────────────────────
type Validation = S<'DemandeValidation'> & { demandeParId?: string; permission?: string };

export function PageValidations() {
  const qc = useQueryClient();
  const toast = useToast();
  const { session } = useSession();
  const [statut, setStatut] = useState('EN_ATTENTE_VALIDATION');
  const [rejet, setRejet] = useState<Validation | null>(null);
  const q = useQuery({ queryKey: ['admin', 'validations', statut], queryFn: () => get<Validation[]>('/admin/validations', { statut: statut || undefined }) });
  const fin = () => { void qc.invalidateQueries({ queryKey: ['admin', 'validations'] }); void qc.invalidateQueries({ queryKey: ['admin', 'utilisateurs'] }); };
  const approuver = useMutation({
    mutationFn: (v: Validation) => post<Validation>(`/admin/validations/${v.id}/approuver`, {}, { version: v.version }),
    onSuccess: (v) => { fin(); toast({ ton: 'succes', titre: 'Demande approuvée', texte: v.resume }); },
  });
  const rejeter = useMutation({
    mutationFn: ({ v, motif }: { v: Validation; motif: string }) => post<Validation>(`/admin/validations/${v.id}/rejeter`, { motif }, { version: v.version }),
    onSuccess: () => { setRejet(null); fin(); toast({ ton: 'info', titre: 'Demande rejetée' }); },
  });
  const moi = session?.utilisateur.id;
  return (
    <>
      <EntetePage titre="Validations à quatre yeux" sousTitre="Les actions sensibles ◆ exigent l’accord d’un second utilisateur habilité, distinct du demandeur." />
      <div className="my-pile">
        <div style={{ maxWidth: 320 }}>
          <SelectField label="Statut" value={statut} onChange={(e) => setStatut(e.target.value)} options={[{ value: 'EN_ATTENTE_VALIDATION', label: 'En attente' }, { value: 'APPROUVEE', label: 'Approuvées' }, { value: 'REJETEE', label: 'Rejetées' }, { value: 'EXPIREE', label: 'Expirées' }, { value: '', label: 'Toutes' }]} />
        </div>
        <ErreurFormulaire erreur={approuver.error} />
        <EtatRequete requete={q} vide={(l) => !l.length} titreVide="Aucune demande" texteVide="Aucune demande ne correspond à ce filtre." enfants={(l) => (
          <Table<Validation>
            legende="Demandes de validation"
            cle={(v) => v.id!}
            lignes={l}
            colonnes={[
              { cle: 't', titre: 'Type', rendu: (v) => <Badge ton="accent">{LIBELLE_TYPE_VALIDATION[v.type ?? ''] ?? v.type}</Badge> },
              { cle: 'r', titre: 'Demande', rendu: (v) => <span className="my-pile my-pile--serre"><span>{v.resume}</span><span className="my-petit my-secondaire">par {v.demandePar} · {dateHeure(v.demandeLe)} · expire {dateHeure(v.expireLe)}</span></span> },
              { cle: 's', titre: 'Statut', rendu: (v) => <span className="my-pile my-pile--serre"><StatutBadge statut={v.statut ?? ''} />{v.traitePar && <span className="my-petit my-secondaire">par {v.traitePar}</span>}{v.motifRejet && <span className="my-petit">{v.motifRejet}</span>}</span> },
              {
                cle: 'a', titre: 'Actions', actions: true, rendu: (v) => v.statut !== 'EN_ATTENTE_VALIDATION' ? null : v.demandeParId === moi
                  ? <span className="my-petit my-secondaire">Votre demande : un autre habilité doit la valider</span>
                  : (
                    <span className="my-ligne" style={{ justifyContent: 'flex-end' }}>
                      <Button compact chargement={approuver.isPending && approuver.variables?.id === v.id} onClick={() => approuver.mutate(v)} aria-label={`Approuver : ${v.resume}`}>Approuver</Button>
                      <Button compact variante="tertiaire" onClick={() => setRejet(v)} aria-label={`Rejeter : ${v.resume}`}>Rejeter</Button>
                    </span>
                  ),
              },
            ]}
          />
        )} />
      </div>
      <Confirmation ouverte={!!rejet} titre="Rejeter la demande" texte={rejet?.resume} libelleAction="Rejeter" danger motif="obligatoire"
        onConfirmer={(motif) => rejet && rejeter.mutate({ v: rejet, motif })} onFermer={() => setRejet(null)} chargement={rejeter.isPending} erreur={rejeter.error} />
    </>
  );
}

// ───────────────────────────────── Utilisateurs internes ─────────────────────────────────
type UtilisateurInterne = S<'UtilisateurInterne'>;
type Affectation = { roleCode: Role; porteeType: 'GLOBALE' | 'ENTREPOT'; porteeIds: string[] };
interface Entrepot { id: string; code: string; nom: string }

function useEntrepots() {
  return useQuery({ queryKey: ['admin', 'entrepots', 'liste-courte'], queryFn: () => get<Entrepot[] | { donnees: Entrepot[] }>('/admin/entrepots').then((r) => (Array.isArray(r) ? r : r.donnees)), retry: false });
}

function EditeurAffectation({ valeur, onChange }: { valeur: Affectation; onChange: (a: Affectation) => void }) {
  const entrepots = useEntrepots();
  return (
    <div className="my-pile">
      <SelectField label="Rôle" value={valeur.roleCode} onChange={(e) => onChange({ ...valeur, roleCode: e.target.value as Role })} options={ROLES_INTERNES.map((r) => ({ value: r, label: LIBELLE_ROLE[r] }))} />
      <fieldset className="my-pile my-pile--serre" style={{ border: 0, padding: 0, margin: 0 }}>
        <legend className="my-field__label">Portée</legend>
        <Choice type="radio" name="portee" label="Globale (tous les entrepôts)" checked={valeur.porteeType === 'GLOBALE'} onChange={() => onChange({ ...valeur, porteeType: 'GLOBALE', porteeIds: [] })} />
        <Choice type="radio" name="portee" label="Limitée à des entrepôts" checked={valeur.porteeType === 'ENTREPOT'} onChange={() => onChange({ ...valeur, porteeType: 'ENTREPOT' })} />
        {valeur.porteeType === 'ENTREPOT' && (
          entrepots.isError ? <Alert ton="alerte">Liste des entrepôts indisponible.</Alert> : (
            <div className="my-ligne">
              {(entrepots.data ?? []).map((e) => (
                <Choice key={e.id} label={`${e.code} — ${e.nom}`} checked={valeur.porteeIds.includes(e.id)}
                  onChange={(ev) => onChange({ ...valeur, porteeIds: ev.target.checked ? [...valeur.porteeIds, e.id] : valeur.porteeIds.filter((x) => x !== e.id) })} />
              ))}
            </div>
          )
        )}
      </fieldset>
    </div>
  );
}

export function PageUtilisateursInternes() {
  const qc = useQueryClient();
  const toast = useToast();
  const { session } = useSession();
  const entrepots = useEntrepots();
  const [page, setPage] = useState(1);
  const [recherche, setRecherche] = useState('');
  const [creation, setCreation] = useState<{ email: string; prenom: string; nom: string; aff: Affectation } | null>(null);
  const [selection, setSelection] = useState<UtilisateurInterne | null>(null);
  const q = useQuery({ queryKey: ['admin', 'utilisateurs', page, recherche], queryFn: () => get<{ donnees: UtilisateurInterne[]; total: number; page: number; taille: number }>('/admin/utilisateurs', { page, taille: 20, q: recherche || undefined }) });
  const creer = useMutation({
    mutationFn: () => post<S<'DemandeValidation'>>('/admin/utilisateurs', { email: creation!.email.trim(), prenom: creation!.prenom.trim(), nom: creation!.nom.trim(), affectations: [creation!.aff] }),
    onSuccess: () => { setCreation(null); toast({ ton: 'info', titre: 'Demande de création envoyée', texte: 'Un second super-administrateur doit la valider (◆).' }); void qc.invalidateQueries({ queryKey: ['admin', 'validations'] }); },
  });
  const nomEntrepot = (id: string) => entrepots.data?.find((e) => e.id === id)?.code ?? id.slice(0, 6);
  return (
    <>
      <EntetePage titre="Utilisateurs internes" sousTitre="Comptes du personnel MYRON : MFA obligatoire, rôles et portées par entrepôt." actions={<Button icone={UserPlus} onClick={() => setCreation({ email: '', prenom: '', nom: '', aff: { roleCode: 'PREPARATEUR', porteeType: 'GLOBALE', porteeIds: [] } })}>Nouveau compte</Button>} />
      <div className="my-pile">
        <div style={{ maxWidth: 420 }}><SearchField label="Rechercher un utilisateur" placeholder="Nom ou e-mail" value={recherche} onChange={(e) => { setRecherche(e.target.value); setPage(1); }} /></div>
        <EtatRequete requete={q} vide={(r) => !r.donnees.length} titreVide="Aucun utilisateur" enfants={(r) => (
          <>
            <Table<UtilisateurInterne>
              legende="Utilisateurs internes"
              cle={(u) => u.id!}
              lignes={r.donnees}
              colonnes={[
                { cle: 'n', titre: 'Utilisateur', rendu: (u) => <span className="my-pile my-pile--serre"><strong>{u.prenom} {u.nom}</strong><span className="my-petit my-secondaire">{u.email}</span></span> },
                { cle: 'r', titre: 'Rôles et portée', rendu: (u) => (u.affectations ?? []).map((a) => `${LIBELLE_ROLE[a.roleCode as Role]} · ${a.porteeType === 'ENTREPOT' ? (a.porteeIds ?? []).map(nomEntrepot).join(', ') : 'globale'}`).join(' ; ') },
                { cle: 'm', titre: 'MFA', rendu: (u) => (u.mfaActive ? <Badge ton="succes">Enrôlée</Badge> : <Badge ton="alerte">À enrôler</Badge>) },
                { cle: 's', titre: 'Statut', rendu: (u) => <StatutBadge statut={u.statut === 'ACTIF' ? 'ACTIF' : u.statut === 'INVITE' ? 'EN_ATTENTE' : u.statut === 'VERROUILLE' ? 'SUSPENDUE' : 'INACTIF'} /> },
                { cle: 'a', titre: 'Actions', actions: true, rendu: (u) => <Button compact variante="secondaire" onClick={() => setSelection(u)} aria-label={`Gérer ${u.email}`}>{u.id === session?.utilisateur.id ? 'Voir' : 'Gérer'}</Button> },
              ]}
            />
            <Pagination page={r.page} total={r.total} taille={r.taille} onPage={setPage} />
          </>
        )} />
      </div>
      <Modal ouverte={!!creation} titre="Nouveau compte interne" onFermer={() => setCreation(null)} large
        actions={<><Button variante="tertiaire" onClick={() => setCreation(null)}>Annuler</Button><Button chargement={creer.isPending} disabled={!creation?.email || !creation.prenom || !creation.nom} onClick={() => creer.mutate()}>Demander la création ◆</Button></>}>
        {creation && (
          <div className="my-form">
            <Alert ton="info">La création est soumise à validation par un second super-administrateur. Le nouvel utilisateur reçoit une invitation et devra activer la MFA à sa première connexion.</Alert>
            <TextField label="Adresse e-mail" type="email" required value={creation.email} onChange={(e) => setCreation({ ...creation, email: e.target.value })} erreur={creer.error instanceof ApiErreur ? creer.error.champs.email : undefined} />
            <div className="my-form-ligne">
              <TextField label="Prénom" required value={creation.prenom} onChange={(e) => setCreation({ ...creation, prenom: e.target.value })} />
              <TextField label="Nom" required value={creation.nom} onChange={(e) => setCreation({ ...creation, nom: e.target.value })} />
            </div>
            <EditeurAffectation valeur={creation.aff} onChange={(aff) => setCreation({ ...creation, aff })} />
            <ErreurFormulaire erreur={creer.error} />
          </div>
        )}
      </Modal>
      {selection && <GestionUtilisateur u={selection} soi={selection.id === session?.utilisateur.id} onFermer={() => setSelection(null)} />}
    </>
  );
}

function GestionUtilisateur({ u, soi, onFermer }: { u: UtilisateurInterne; soi: boolean; onFermer: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const premiere = u.affectations?.[0];
  const [aff, setAff] = useState<Affectation>({ roleCode: (premiere?.roleCode as Role) ?? 'PREPARATEUR', porteeType: (premiere?.porteeType as Affectation['porteeType']) ?? 'GLOBALE', porteeIds: premiere?.porteeIds ?? [] });
  const [action, setAction] = useState<null | 'statut' | 'mfa' | 'sessions'>(null);
  const rafraichir = () => { void qc.invalidateQueries({ queryKey: ['admin', 'utilisateurs'] }); void qc.invalidateQueries({ queryKey: ['admin', 'validations'] }); };
  const affectations = useMutation({
    mutationFn: () => put(`/admin/utilisateurs/${u.id}/affectations`, [aff], { version: u.version }),
    onSuccess: () => { rafraichir(); onFermer(); toast({ ton: 'info', titre: 'Modification soumise à validation (◆)' }); },
  });
  const statut = useMutation({
    mutationFn: (motif: string) => post(`/admin/utilisateurs/${u.id}/statut`, { action: u.statut === 'DESACTIVE' ? 'REACTIVER' : u.statut === 'VERROUILLE' ? 'DEVERROUILLER' : 'DESACTIVER', motif }, { version: u.version }),
    onSuccess: () => { rafraichir(); onFermer(); toast({ ton: 'succes', titre: 'Statut mis à jour' }); },
  });
  const mfa = useMutation({
    mutationFn: (motif: string) => post(`/admin/utilisateurs/${u.id}/mfa/reinitialiser`, { motif }),
    onSuccess: () => { rafraichir(); onFermer(); toast({ ton: 'info', titre: 'Réinitialisation MFA soumise à validation (◆)' }); },
  });
  const sessions = useMutation({
    mutationFn: () => del(`/admin/utilisateurs/${u.id}/sessions`),
    onSuccess: () => { setAction(null); toast({ ton: 'succes', titre: 'Sessions révoquées' }); },
  });
  const libelleStatut = u.statut === 'DESACTIVE' ? 'Réactiver le compte' : u.statut === 'VERROUILLE' ? 'Déverrouiller le compte' : 'Désactiver le compte';
  return (
    <Modal ouverte titre={`${u.prenom} ${u.nom}`} onFermer={onFermer} large actions={<Button variante="tertiaire" onClick={onFermer}>Fermer</Button>}>
      <div className="my-pile">
        <dl className="my-dl"><dt>E-mail</dt><dd>{u.email}</dd><dt>MFA</dt><dd>{u.mfaActive ? 'Enrôlée' : 'À enrôler à la prochaine connexion'}</dd><dt>Dernière connexion</dt><dd>{dateHeure(u.derniereConnexionLe)}</dd></dl>
        {soi ? <Alert ton="info">Vous ne pouvez pas modifier vos propres droits ni votre propre MFA.</Alert> : (
          <>
            <h3 className="my-titre-4">Rôle et portée ◆</h3>
            <EditeurAffectation valeur={aff} onChange={setAff} />
            <div><Button variante="secondaire" chargement={affectations.isPending} onClick={() => affectations.mutate()}>Soumettre la modification</Button></div>
            <ErreurFormulaire erreur={affectations.error} />
            <hr className="my-separateur" />
            <h3 className="my-titre-4">Sécurité</h3>
            <div className="my-ligne">
              <Button variante="secondaire" icone={RotateCcw} onClick={() => setAction('mfa')}>Réinitialiser la MFA ◆</Button>
              <Button variante="secondaire" icone={KeyRound} onClick={() => setAction('sessions')}>Révoquer les sessions</Button>
              <Button variante={u.statut === 'ACTIF' ? 'danger' : 'primaire'} onClick={() => setAction('statut')}>{libelleStatut}</Button>
            </div>
          </>
        )}
      </div>
      <Confirmation ouverte={action === 'mfa'} titre="Réinitialiser la MFA ?" texte="Vérifiez l’identité de la personne hors bande (appel, présence). Un second super-administrateur devra valider ; ses sessions seront ensuite révoquées et l’enrôlement redemandé." libelleAction="Soumettre ◆" motif="obligatoire" onConfirmer={(m) => mfa.mutate(m)} onFermer={() => setAction(null)} chargement={mfa.isPending} erreur={mfa.error} />
      <Confirmation ouverte={action === 'sessions'} titre="Révoquer toutes les sessions ?" texte="L’utilisateur sera déconnecté de tous ses appareils." libelleAction="Révoquer" danger onConfirmer={() => sessions.mutate()} onFermer={() => setAction(null)} chargement={sessions.isPending} erreur={sessions.error} />
      <Confirmation ouverte={action === 'statut'} titre={libelleStatut} libelleAction="Confirmer" danger={u.statut === 'ACTIF'} motif="obligatoire" onConfirmer={(m) => statut.mutate(m)} onFermer={() => setAction(null)} chargement={statut.isPending} erreur={statut.error} />
    </Modal>
  );
}

// ───────────────────────────────── Rôles (lecture seule) ─────────────────────────────────
export function PageRoles() {
  const q = useQuery({ queryKey: ['admin', 'roles'], queryFn: () => get<S<'Role'>[]>('/admin/roles') });
  return (
    <>
      <EntetePage titre="Rôles et permissions" sousTitre="Matrice exécutable (refus par défaut). Lecture seule : toute évolution passe par une revue de sécurité." />
      <EtatRequete requete={q} enfants={(l) => (
        <div className="my-grille" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
          {l.map((r) => (
            <Card key={r.code} as="article" aria-labelledby={`role-${r.code}`}>
              <div className="my-pile my-pile--serre">
                <div className="my-ligne my-ligne--espace"><h2 id={`role-${r.code}`} className="my-titre-4">{r.libelle}</h2><Badge ton={r.type === 'INTERNE' ? 'marque' : 'sable'}>{r.type === 'INTERNE' ? 'Interne · MFA obligatoire' : 'Client'}</Badge></div>
                <ul className="my-petit" style={{ margin: 0, paddingLeft: 18 }}>{(r.permissions ?? []).map((p) => <li key={p}><code>{p}</code></li>)}</ul>
              </div>
            </Card>
          ))}
        </div>
      )} />
    </>
  );
}

// ───────────────────────────────── Clés de chiffrement (SIMULÉ) ─────────────────────────────────
type Cle = S<'CleChiffrementMeta'> & { simulee?: boolean; note?: string };

export function PageClesChiffrement() {
  const toast = useToast();
  const q = useQuery({ queryKey: ['admin', 'cles'], queryFn: () => get<Cle[]>('/admin/securite/cles') });
  const rotation = useMutation({ mutationFn: () => post('/admin/securite/cles/rotation'), onSuccess: () => toast({ ton: 'info', titre: 'Demande de rotation soumise (◆)', texte: 'Simulation : aucune clé réelle ne sera modifiée.' }) });
  return (
    <>
      <EntetePage titre="Clés de chiffrement" sousTitre="Chiffrement applicatif AES-256-GCM des champs sensibles, clés versionnées." />
      <div className="my-pile">
        <Alert ton="alerte" titre="SIMULÉ — non implémenté dans la maquette">
          Aucune clé n’existe côté navigateur et aucun champ n’est chiffré : les valeurs sensibles sont stockées en clair, préfixées « SIMULE: ». Le contrat de la phase 2 (enveloppe v{'{n}'}:iv:tag:ct, AAD, KEK/DEK, rotation progressive) est décrit dans docs/securite/CONTRATS_SECURITE_PRODUCTION.md.
        </Alert>
        <EtatRequete requete={q} enfants={(l) => (
          <Table<Cle>
            legende="Versions de clé"
            cle={(c) => String(c.version)}
            lignes={l}
            colonnes={[
              { cle: 'v', titre: 'Version', rendu: (c) => `v${c.version}` },
              { cle: 'a', titre: 'Algorithme', rendu: (c) => c.algorithme },
              { cle: 'e', titre: 'Champs concernés', num: true, rendu: (c) => c.nbEnveloppes },
              { cle: 's', titre: 'État', rendu: (c) => <span className="my-ligne">{c.active ? <Badge ton="succes">Active</Badge> : <Badge>Retirée</Badge>}<MentionSimule>SIMULÉ</MentionSimule></span> },
            ]}
          />
        )} />
        <div><Button variante="secondaire" icone={ShieldCheck} chargement={rotation.isPending} onClick={() => rotation.mutate()}>Demander une rotation ◆</Button></div>
        <ErreurFormulaire erreur={rotation.error} />
      </div>
    </>
  );
}
